import { useEffect, useState } from "react";

import { Center } from "@/components/Center";
import { Column } from "@/components/Column";
import { ErrorText } from "@/components/ErrorText";
import { LoadingIndicator } from "@/components/LoadingIndicator";
import { Row } from "@/components/Row";
import { TopPriorityCandidateBadge } from "@/components/TopPriorityCandidateBadge";
import { AgentSchedulingGuardrailDialog } from "@/components/AgentSchedulingGuardrailDialog";
import { ApplicationReviewModalActions } from "@/components/application-review/ApplicationReviewModalActions";
import { RouteThroughStevieDialog } from "@/components/application-review/RouteThroughStevieDialog";
import { CandidacyRejectAction } from "@jobs/candidate-sheet-v2/CandidacyRejectAction";
import { CandidateSheetV2 } from "@jobs/candidate-sheet-v2/CandidateSheetV2";
import { PipelineEntryChip } from "@jobs/pipeline/shared/PipelineEntryChip";
import { ShortlistTakeoverDialog } from "@jobs/pipeline/shared/ShortlistTakeoverDialog";

import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import { useApplicationReviewDecision } from "@/hooks/useApplicationReviewDecision";
import { useGetCandidacyInfo } from "@/hooks/queries/useGetCandidacyInfo";
import { useGetCandidacySheetData } from "@/hooks/queries/useGetCandidacySheetData";
import { useGetJobInternalConfig } from "@/hooks/queries/useGetJobInternalConfig";
import { useGetUserSettings } from "@/hooks/queries/useGetUserSettings";
import { agentGuardrails } from "@/lib/agentGuardrails";
import {
  getSourcedType,
  sourcedTypeToDotColor,
  sourcedTypeToLabel,
} from "@/lib/constants";
import { formatting } from "@/lib/formatting";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { screeningAvailability } from "@/lib/screeningAvailability";
import {
  CallNowBadgeVariant,
  CandidacySource,
  CandidacyStatus,
  CandidateSheetVariant,
  Job,
  ReviewDecision,
  ShortlistTakeover,
  StevieRouteEligibility,
  StevieRouteMode,
} from "@/types";

// Keeps the Stevie option disabled, with an explanation, until candidacy-info
// answers with the real verdict.
const loadingStevieRoute: StevieRouteEligibility = {
  mode: StevieRouteMode.BLOCKED,
  reason: "Still checking whether Stevie can text this candidate.",
};

// The decision bar plus the full V2 candidate sheet, mounted frameless inside
// the review dialog.
export const ReviewV2Candidate = ({
  candidacyId,
  canMoveToNext,
  canMoveToPrevious,
  job,
  onActioned,
  onClose,
  onMoveToNext,
  onMoveToPrevious,
  userId,
}: {
  candidacyId: string;
  canMoveToNext: boolean;
  canMoveToPrevious: boolean;
  job: Job;
  onActioned: (payload: {
    agentActive: boolean;
    candidacyId: string;
    candidateName: string;
    status: CandidacyStatus;
  }) => void;
  onClose: () => void;
  onMoveToNext: () => void;
  onMoveToPrevious: () => void;
  userId: number;
}) => {
  /* Database CRUD Operations */
  // Read
  const { data: candidacyInfo, isLoading: isCandidacyLoading } =
    useGetCandidacyInfo({ candidacyId });
  const profileQuery = useGetCandidacySheetData({
    jobId: job.id,
    userId: candidacyInfo?.talentId ?? 0,
  });
  const profileData = profileQuery.data;
  const { data: jobInternalConfig } = useGetJobInternalConfig();
  const { data: userSettings } = useGetUserSettings();
  const { userNameMap } = useTaggableUsers();

  // Update
  const { decide: decideApplicationReview, isPending: isUpdating } =
    useApplicationReviewDecision();

  /* React Hooks */
  const [takeover, setTakeover] = useState<ShortlistTakeover | null>(null);
  const [agentGuardrailReasons, setAgentGuardrailReasons] = useState<
    string[] | null
  >(null);
  const [isStevieDialogOpen, setIsStevieDialogOpen] = useState(false);
  // A shortlist shortcut can advance the queue under an open dialog; don't let
  // a note typed for one candidate get sent for the next.
  useEffect(() => {
    setIsStevieDialogOpen(false);
  }, [candidacyId]);

  /* Helpers */
  // The decision toasts name the candidate, so hold the actions until the
  // profile lands rather than announcing a blank name mid-queue.
  const isProfileLoading =
    !!candidacyInfo?.talentId &&
    profileQuery.isPending &&
    !profileQuery.isError;
  // Disable if the AI scheduling agent is on and the user has no availability.
  const isShortlistDisabled =
    candidacyInfo != null &&
    screeningAvailability.blocksShortlist({
      candidacyStatus: candidacyInfo.status,
      jobAgentActive: !!jobInternalConfig?.stafferAgentActive,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      availability: userSettings?.screeningAvailability || null,
    });

  // How an Applied candidacy got here (Resurrected, Internally Sourced, back
  // from Stevie...). The pipeline shows this in place of the stage chip; the
  // review portal hides the status dropdown that would otherwise carry it.
  const sourcedType =
    candidacyInfo && candidacyInfo.status === CandidacyStatus.APPLIED
      ? getSourcedType(
          candidacyInfo.source,
          candidacyInfo.isExternallySourced,
          candidacyInfo.isAutoResurrected,
          candidacyInfo.stevieNudgedAt !== null &&
            candidacyInfo.stevieCallEndedAt !== null,
        )
      : null;
  const showsTopPriorityBadge =
    !!candidacyInfo &&
    (candidacyInfo.isTopPriority ||
      candidacyInfo.source === CandidacySource.SMARTSOURCING);

  const ownerUserId =
    candidacyInfo && candidacyInfo.candidacyOwners.length > 0
      ? candidacyInfo.candidacyOwners[0].userId
      : null;
  // Only the owner (or anyone, when unowned) may send the candidate to Stevie:
  // the text goes out under the sender's name, so it can't cut across another
  // staffer's conversation. Surfaced as a BLOCKED verdict so the menu item
  // disables with the reason on hover, like the server-side refusals.
  const isOwnedByAnotherStaffer =
    ownerUserId !== null && ownerUserId !== userId;
  const eligibilityForStevieOutreach: StevieRouteEligibility =
    isOwnedByAnotherStaffer
      ? {
          mode: StevieRouteMode.BLOCKED,
          reason: `This candidacy is owned by ${userNameMap[ownerUserId]}.`,
        }
      : (candidacyInfo?.eligibilityForStevieOutreach ?? loadingStevieRoute);
  // Null when the route is blocked, which is also when the dialog can't open.
  const { mode } = eligibilityForStevieOutreach;
  const stevieRouteMode = [
    StevieRouteMode.NEW,
    StevieRouteMode.RENUDGE,
  ].includes(mode)
    ? mode
    : null;

  /* Handlers */
  const applyDecision = (decision: ReviewDecision) => {
    if (!candidacyInfo) return;
    const candidateName = [
      profileData?.profile.firstName,
      profileData?.profile.lastName,
    ]
      .filter((s): s is string => Boolean(s))
      .join(" ");

    decideApplicationReview({
      candidacy: {
        id: candidacyId,
        // The current status is the last change's toStatus (or the initial
        // status when no change rows exist yet).
        lastCandidacyStatusChange: { toStatus: candidacyInfo.status },
      },
      decision,
      // Rapid-fire review would otherwise refetch the whole dashboard per
      // keystroke; the modal batches one invalidate when it closes.
      deferDashboardInvalidate: true,
      screen: MIXPANEL_SCREENS.APPLICATION_REVIEW_PORTAL,
      onSuccess: ({ status }) => {
        onActioned({
          // Manual mode shortlists without the agent, so it isn't "handling" this one.
          agentActive:
            decision === ReviewDecision.SHORTLIST_MANUAL
              ? false
              : (jobInternalConfig?.stafferAgentActive ?? false),
          candidacyId,
          candidateName,
          status,
        });
      },
    });
  };

  // The decision after guardrail/takeover interstitials have been cleared.
  const proceedWithDecision = (decision: ReviewDecision) => {
    // Re-check the action guards — dialog callbacks can fire while a mutation
    // is already pending (double-click) or after state has gone stale.
    if (!candidacyInfo || isUpdating || isProfileLoading) return;
    if (decision === ReviewDecision.SHORTLIST && isShortlistDisabled) return;
    // Shortlisting another staffer's candidacy prompts a takeover choice first
    // (both agent and manual shortlist).
    const isShortlistDecision =
      decision === ReviewDecision.SHORTLIST ||
      decision === ReviewDecision.SHORTLIST_MANUAL;
    if (isShortlistDecision && ownerUserId !== null && ownerUserId !== userId) {
      setTakeover({
        candidacyId,
        ownerUserId,
        onShortlist: () => {
          applyDecision(decision);
        },
      });
      return;
    }

    applyDecision(decision);
  };

  const handleAction = (decision: ReviewDecision) => {
    if (!candidacyInfo || isUpdating || isProfileLoading) return;
    if (decision === ReviewDecision.SHORTLIST && isShortlistDisabled) return;

    // Agentic shortlists get a confirmation when a guardrail applies (past
    // submit stage, human outreach in the last 24h). Manual mode skips it.
    if (
      decision === ReviewDecision.SHORTLIST &&
      jobInternalConfig?.stafferAgentActive
    ) {
      const reasons = agentGuardrails.getBlockReasons({
        candidacyStatus: candidacyInfo.status,
        activePipelines: candidacyInfo.activePipelines,
        lastHumanCommunication: candidacyInfo.lastHumanCommunication,
        lastCommunication: candidacyInfo.lastCommunication,
      });
      if (reasons.length > 0) {
        setAgentGuardrailReasons(reasons);
        return;
      }
    }

    proceedWithDecision(decision);
  };

  // Stevie texts to learn more. The dialog owns the send — the text goes out
  // under the staffer's own name, so it gets confirmed first.
  const handleRouteThroughStevie = () => {
    setIsStevieDialogOpen(true);
  };

  // Routing is a decision too: the candidacy is Stevie's until the call lands,
  // so it leaves the queue and the reviewer moves on, same as shortlist/reject.
  // The status itself stays Applied — no decision toast, nothing to undo.
  const handleRoutedThroughStevie = () => {
    onActioned({
      agentActive: false,
      candidacyId,
      candidateName: formatting.fullName(
        profileData?.profile.firstName,
        profileData?.profile.lastName,
        "Candidate",
      ),
      status: CandidacyStatus.APPLIED,
    });
  };

  /* Render */
  // Rejecting from the portal captures a reason, same as the sheet's own
  // reject flow — staffers always say why.
  const renderRejectControl = () => {
    // Same name-in-toast reason as handleAction.
    if (!candidacyInfo || isProfileLoading) return null;

    return (
      <CandidacyRejectAction
        candidacyInfo={candidacyInfo}
        deferDashboardInvalidate
        displayVariant="review"
        mixpanelScreen={MIXPANEL_SCREENS.APPLICATION_REVIEW_PORTAL}
        onRejected={({ status }) => {
          onActioned({
            agentActive: false,
            candidacyId,
            candidateName: formatting.fullName(
              profileData?.profile.firstName,
              profileData?.profile.lastName,
              "Candidate",
            ),
            status,
          });
        }}
      />
    );
  };

  const renderSheet = () => {
    if (isCandidacyLoading || !candidacyInfo) {
      return (
        <Center className="min-h-0 flex-1">
          <LoadingIndicator variant="large" />
        </Center>
      );
    }

    const workerId = candidacyInfo.talentId ?? null;
    if (workerId === null) {
      return (
        <Center className="min-h-0 flex-1">
          <ErrorText errorMessage="This candidacy has no talent record to display." />
        </Center>
      );
    }

    return (
      <Column className="min-h-0 flex-1 overflow-hidden">
        <CandidateSheetV2
          candidacyId={candidacyId}
          canMoveToNextCandidacy={canMoveToNext}
          canMoveToPreviousCandidacy={canMoveToPrevious}
          handleMoveToNextCandidacy={onMoveToNext}
          handleMoveToPreviousCandidacy={onMoveToPrevious}
          job={job}
          onClose={onClose}
          variant={CandidateSheetVariant.EMBEDDED}
          workerId={workerId}
        />
      </Column>
    );
  };

  const render = () => {
    return (
      // Decisions sit inside the candidate card, not on the modal's grey
      // backdrop — they read as part of the candidate, not the chrome.
      <Column className="min-h-0 flex-1 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <Column className="relative shrink-0 px-6 pt-5">
          <ApplicationReviewModalActions
            eligibilityForStevieOutreach={eligibilityForStevieOutreach}
            isAgentActive={jobInternalConfig?.stafferAgentActive ?? false}
            isShortlistDisabled={isShortlistDisabled}
            onAction={handleAction}
            onRouteThroughStevie={handleRouteThroughStevie}
            rejectControl={renderRejectControl()}
            showCalendarWarning={isShortlistDisabled}
          />
          {candidacyInfo && (sourcedType || showsTopPriorityBadge) ? (
            <Row className="absolute right-6 top-5 items-center gap-2">
              {sourcedType ? (
                <PipelineEntryChip
                  dotClassName={sourcedTypeToDotColor[sourcedType]}
                  label={sourcedTypeToLabel[sourcedType]}
                />
              ) : null}
              {showsTopPriorityBadge ? (
                <TopPriorityCandidateBadge
                  variant={
                    candidacyInfo.isTopPriority
                      ? CallNowBadgeVariant.TOP_PRIORITY
                      : CallNowBadgeVariant.SMART_SOURCED
                  }
                />
              ) : null}
            </Row>
          ) : null}
        </Column>
        {renderSheet()}
        {agentGuardrailReasons && (
          <AgentSchedulingGuardrailDialog
            onClose={() => {
              setAgentGuardrailReasons(null);
            }}
            onShortlistManually={() => {
              proceedWithDecision(ReviewDecision.SHORTLIST_MANUAL);
            }}
            onUseAgent={() => {
              proceedWithDecision(ReviewDecision.SHORTLIST);
            }}
            reasons={agentGuardrailReasons}
            screenName={MIXPANEL_SCREENS.APPLICATION_REVIEW_PORTAL}
          />
        )}
        {isStevieDialogOpen && stevieRouteMode !== null && (
          <RouteThroughStevieDialog
            candidacyId={candidacyId}
            candidateFirstName={
              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
              profileData?.profile.firstName || "this candidate"
            }
            mode={stevieRouteMode}
            onClose={() => {
              setIsStevieDialogOpen(false);
            }}
            onRouted={handleRoutedThroughStevie}
          />
        )}
        {takeover && (
          <ShortlistTakeoverDialog
            {...takeover}
            onClose={() => {
              setTakeover(null);
            }}
          />
        )}
      </Column>
    );
  };

  return render();
};
